import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverterV2 from '@careerstart/wae-common/src/main/helperFunction/LanguageConverterV2';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  GlobalStyles,
  IconButton,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import {
  Autocomplete as GoogleAutocomplete,
  Circle,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';

import googleMapsLoaderConfig from '../../../../components/Map/googleMapsLoaderConfig';
import { ACTION_BLUE, BLACK } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';

const MILES_TO_METERS = 1609.344;
const DEFAULT_RADIUS_MILES = 25;
const MIN_RADIUS = 1;
const MAX_RADIUS = 250;
const DEFAULT_CENTER = { lat: 40.7128, lng: -74.006 }; // New York City fallback center
const AUTOCOMPLETE_FIELDS = ['address_components', 'geometry', 'formatted_address', 'name'];

const RADIUS_TYPE = {
  SUGGESTED: 'suggested',
  CUSTOM: 'custom',
};

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
};

const getAddressComponent = (components, type, useShortName = false) => {
  const match = components?.find((component) => component.types?.includes(type));
  if (!match) return '';
  return useShortName ? match.short_name : match.long_name;
};

const parseCityState = (place) => {
  const components = place?.address_components || [];
  const city =
    getAddressComponent(components, 'locality') ||
    getAddressComponent(components, 'sublocality') ||
    getAddressComponent(components, 'postal_town') ||
    getAddressComponent(components, 'administrative_area_level_2');
  const state = getAddressComponent(components, 'administrative_area_level_1', true);
  return { city, state };
};

const DistanceFilterModal = ({ open, onClose, onApply, initialValues }) => {
  const { isLoaded } = useJsApiLoader(googleMapsLoaderConfig);

  const [center, setCenter] = useState(initialValues?.center || DEFAULT_CENTER);
  const [radiusMiles, setRadiusMiles] = useState(
    initialValues?.radiusMiles || DEFAULT_RADIUS_MILES
  );
  const [radiusType, setRadiusType] = useState(initialValues?.radiusType || RADIUS_TYPE.SUGGESTED);
  const [locationName, setLocationName] = useState(initialValues?.locationName || '');
  const [city, setCity] = useState(initialValues?.city || '');
  const [state, setState] = useState(initialValues?.state || '');
  const [mapRef, setMapRef] = useState(null);
  const autocompleteRef = useRef(null);
  // Invalidates in-flight geolocation / reverse-geocode when a newer location intent starts.
  const locationRequestIdRef = useRef(0);

  const beginLocationRequest = useCallback(() => {
    locationRequestIdRef.current += 1;
    return locationRequestIdRef.current;
  }, []);

  const setLocation = useCallback(
    ({ lat, lng, locationName: name, city: nextCity, state: nextState }) => {
      setCenter({ lat, lng });
      setLocationName(name);
      setCity(nextCity);
      setState(nextState);
    },
    []
  );

  const setFallbackToNewYorkCity = useCallback(() => {
    beginLocationRequest();
    setLocation({
      lat: DEFAULT_CENTER.lat,
      lng: DEFAULT_CENTER.lng,
      locationName: 'New York, NY, USA',
      city: 'New York',
      state: 'NY',
    });
  }, [beginLocationRequest, setLocation]);

  useEffect(() => {
    if (open) {
      beginLocationRequest();
      setCenter(initialValues?.center || DEFAULT_CENTER);
      setRadiusMiles(initialValues?.radiusMiles || DEFAULT_RADIUS_MILES);
      setRadiusType(initialValues?.radiusType || RADIUS_TYPE.SUGGESTED);
      setLocationName(initialValues?.locationName || '');
      setCity(initialValues?.city || '');
      setState(initialValues?.state || '');
    }
  }, [open, initialValues, beginLocationRequest]);

  const radiusMeters = useMemo(() => radiusMiles * MILES_TO_METERS, [radiusMiles]);

  const fitMapToCircle = useCallback(() => {
    if (!mapRef || !window.google) return;
    const circle = new window.google.maps.Circle({ center, radius: radiusMeters });
    mapRef.fitBounds(circle.getBounds());
  }, [mapRef, center, radiusMeters]);

  useEffect(() => {
    fitMapToCircle();
  }, [fitMapToCircle]);

  const onMapLoad = useCallback((map) => {
    setMapRef(map);
  }, []);

  const onAutocompleteLoad = useCallback((autocomplete) => {
    autocompleteRef.current = autocomplete;
  }, []);

  const applyLocationFromPlace = useCallback(
    (place, lat, lng) => {
      const { city: placeCity, state: placeState } = parseCityState(place);
      setLocation({
        lat,
        lng,
        locationName:
          place?.formatted_address || place?.name || `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
        city: placeCity,
        state: placeState,
      });
    },
    [setLocation]
  );

  const reverseGeocodeAndSetLocation = useCallback(
    (lat, lng) => {
      const requestId = beginLocationRequest();
      // Clear the previous label so Apply cannot persist new coords with an old place name.
      setLocation({ lat, lng, locationName: '', city: '', state: '' });

      const applyResolvedLocation = (name, nextCity, nextState) => {
        if (locationRequestIdRef.current !== requestId) return;
        setLocation({ lat, lng, locationName: name, city: nextCity, state: nextState });
      };

      if (!window.google?.maps?.Geocoder) {
        applyResolvedLocation(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, '', '');
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (locationRequestIdRef.current !== requestId) return;

        if (status === 'OK' && results?.[0]) {
          applyLocationFromPlace(results[0], lat, lng);
          return;
        }
        applyResolvedLocation(`${lat.toFixed(4)}, ${lng.toFixed(4)}`, '', '');
      });
    },
    [applyLocationFromPlace, beginLocationRequest, setLocation]
  );

  useEffect(() => {
    if (!open || initialValues?.center) return undefined;

    const requestId = locationRequestIdRef.current;

    if (!navigator?.geolocation) {
      setFallbackToNewYorkCity();
      return undefined;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (locationRequestIdRef.current !== requestId) return;
        reverseGeocodeAndSetLocation(coords.latitude, coords.longitude);
      },
      () => {
        if (locationRequestIdRef.current !== requestId) return;
        setFallbackToNewYorkCity();
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      }
    );

    return () => {
      // Invalidate this geolocation attempt on cleanup / newer location intent.
      if (locationRequestIdRef.current === requestId) {
        beginLocationRequest();
      }
    };
  }, [
    open,
    initialValues?.center,
    reverseGeocodeAndSetLocation,
    setFallbackToNewYorkCity,
    beginLocationRequest,
  ]);

  const onPlaceChanged = useCallback(() => {
    const autocomplete = autocompleteRef.current;
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place?.geometry?.location) return;

    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    beginLocationRequest();
    applyLocationFromPlace(place, lat, lng);
  }, [applyLocationFromPlace, beginLocationRequest]);

  const onMapClick = useCallback(
    (event) => {
      if (!event?.latLng) return;
      reverseGeocodeAndSetLocation(event.latLng.lat(), event.latLng.lng());
    },
    [reverseGeocodeAndSetLocation]
  );

  const onMarkerDragEnd = useCallback(
    (event) => {
      if (!event?.latLng) return;
      reverseGeocodeAndSetLocation(event.latLng.lat(), event.latLng.lng());
    },
    [reverseGeocodeAndSetLocation]
  );

  const handleRadiusTypeChange = (event) => {
    setRadiusType(event.target.value);
    if (event.target.value === RADIUS_TYPE.SUGGESTED) {
      setRadiusMiles(DEFAULT_RADIUS_MILES);
    }
  };

  const handleSliderChange = (_event, newValue) => {
    setRadiusMiles(newValue);
  };

  const handleMilesInputChange = (event) => {
    const value = event.target.value === '' ? MIN_RADIUS : Number(event.target.value);
    setRadiusMiles(Math.min(Math.max(value, MIN_RADIUS), MAX_RADIUS));
  };

  const handleApply = () => {
    onApply({
      center,
      radiusMiles,
      radiusType,
      locationName,
      city,
      state,
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      PaperProps={{ sx: { borderRadius: '16px', maxWidth: '780px' } }}
    >
      <GlobalStyles
        styles={{
          '.pac-container': {
            zIndex: 1400,
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: '16px 20px 8px',
        }}
      >
        <IconButton onClick={onClose} size="small" sx={{ color: BLACK[100] }}>
          <CloseIcon />
        </IconButton>
        <Typography sx={{ fontFamily: PRIMARY_FONT[700], fontSize: '18px', color: BLACK[100] }}>
          Location
        </Typography>
        <Box sx={{ width: 32 }} />
      </Box>

      <DialogContent sx={{ px: '20px', pb: '20px', pt: '8px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {isLoaded && (
            <GoogleAutocomplete
              onLoad={onAutocompleteLoad}
              onPlaceChanged={onPlaceChanged}
              options={{ fields: AUTOCOMPLETE_FIELDS }}
            >
              <TextField
                key={`location-search-${open}-${locationName || 'empty'}`}
                fullWidth
                size="small"
                placeholder={LanguageConverterV2('userList.filter.distance.selectedCoordinate')}
                defaultValue={locationName}
                InputProps={{
                  sx: {
                    borderRadius: '24px',
                    fontFamily: PRIMARY_FONT[400],
                    fontSize: '14px',
                    backgroundColor: BLACK[10],
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              />
            </GoogleAutocomplete>
          )}

          {isLoaded && (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={10}
              onLoad={onMapLoad}
              onClick={onMapClick}
              options={{
                disableDefaultUI: true,
                zoomControl: true,
                draggableCursor: 'pointer',
              }}
            >
              <Marker position={center} draggable onDragEnd={onMarkerDragEnd} />
              <Circle
                center={center}
                radius={radiusMeters}
                options={{
                  strokeColor: ACTION_BLUE.ACTION_BLUE,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                  fillColor: ACTION_BLUE.ACTION_BLUE,
                  fillOpacity: 0.15,
                  clickable: false,
                }}
              />
            </GoogleMap>
          )}

          <RadioGroup value={radiusType} onChange={handleRadiusTypeChange}>
            <FormControlLabel
              value={RADIUS_TYPE.SUGGESTED}
              control={<Radio />}
              label={
                <Box>
                  <Typography
                    sx={{ fontFamily: PRIMARY_FONT[700], fontSize: '16px', color: BLACK[100] }}
                  >
                    Suggested radius
                  </Typography>
                  <Typography
                    sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', color: BLACK[60] }}
                  >
                    Show me listings from this general area.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 1 }}
            />
            <FormControlLabel
              value={RADIUS_TYPE.CUSTOM}
              control={<Radio />}
              label={
                <Box>
                  <Typography
                    sx={{ fontFamily: PRIMARY_FONT[700], fontSize: '16px', color: BLACK[100] }}
                  >
                    Custom radius
                  </Typography>
                  <Typography
                    sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '14px', color: BLACK[60] }}
                  >
                    Only show me listings within a specific distance.
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start' }}
            />
          </RadioGroup>

          {radiusType === RADIUS_TYPE.CUSTOM && (
            <Box sx={{ px: 1 }}>
              <Slider
                value={radiusMiles}
                onChange={handleSliderChange}
                min={MIN_RADIUS}
                max={MAX_RADIUS}
                sx={{
                  color: ACTION_BLUE.ACTION_BLUE,
                  '& .MuiSlider-thumb': { width: 20, height: 20 },
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography
                  sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '13px', color: BLACK[60] }}
                >
                  {MIN_RADIUS}
                </Typography>
                <Typography
                  sx={{ fontFamily: PRIMARY_FONT[400], fontSize: '13px', color: BLACK[60] }}
                >
                  {MAX_RADIUS}
                </Typography>
              </Box>

              <TextField
                label="Miles"
                type="number"
                size="small"
                value={radiusMiles}
                onChange={handleMilesInputChange}
                inputProps={{ min: MIN_RADIUS, max: MAX_RADIUS }}
                sx={{ width: '100%' }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            fullWidth
            onClick={handleApply}
            disabled={!locationName}
            sx={{
              backgroundColor: ACTION_BLUE.ACTION_BLUE,
              borderRadius: '8px',
              textTransform: 'none',
              fontFamily: PRIMARY_FONT[700],
              fontSize: '16px',
              py: 1.5,
              '&:hover': { backgroundColor: ACTION_BLUE.ACTION_BLUE_HOVER },
            }}
          >
            Apply
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

DistanceFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    center: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number,
    }),
    radiusMiles: PropTypes.number,
    radiusType: PropTypes.string,
    locationName: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
  }),
};

export default DistanceFilterModal;
